// apps/app/src/features/auth/dev-quick-login.tsx
//
// One-click sign-in for the seeded demo accounts.
//
// The accounts are read from VITE_DEV_ACCOUNTS rather than written here. A
// production build has no such variable, so the list is empty and this renders
// nothing — and, more importantly, no working credentials exist anywhere in the
// shipped bundle to be found. Relying on `import.meta.env.DEV` alone is not
// enough: the constants stayed in the output even when the branch was dead.
//
// Format: email|Label|hint, entries separated by ';' — hints contain commas.
// Seeded by prisma/seed.ts. Every account signs in through the same endpoint;
// the role on the session decides what it can reach.
// There is deliberately no cross-tenant superadmin — User.tenantId is required
// and OWNER is the highest role within a single clinic.

import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

interface DemoAccount {
  email: string;
  label: string;
  hint: string;
}

function parseAccounts(raw: string | undefined): DemoAccount[] {
  if (!raw) return [];

  return raw
    .split(';')
    .map((entry) => entry.split('|').map((part) => part.trim()))
    .flatMap(([email, label, hint]) =>
      email && label ? [{ email, label, hint: hint ?? '' }] : [],
    );
}

const ACCOUNTS = parseAccounts(import.meta.env['VITE_DEV_ACCOUNTS'] as string | undefined);
const PASSWORD = (import.meta.env['VITE_DEV_PASSWORD'] as string | undefined) ?? '';

interface DevQuickLoginProps {
  onPick: (email: string, password: string) => void;
  disabled?: boolean;
}

export function DevQuickLogin({ onPick, disabled }: DevQuickLoginProps) {
  const { t } = useTranslation('auth');
  if (!import.meta.env.DEV || ACCOUNTS.length === 0 || !PASSWORD) return null;

  return (
    <div className="space-y-2 rounded-xl border border-dashed border-amber-300 bg-amber-50/60 p-3">
      <p className="text-xs font-medium text-amber-900">{t('devLogin.title')}</p>

      <div className="grid gap-1.5">
        {ACCOUNTS.map((a) => (
          <Button
            key={a.email}
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={() => onPick(a.email, PASSWORD)}
            className="h-auto flex-col items-start gap-0 whitespace-normal border-amber-200 bg-card px-3 py-2 text-left hover:border-amber-400 hover:bg-card"
          >
            <span className="block text-sm font-medium text-foreground">{a.label}</span>
            {a.hint && <span className="block text-xs font-normal text-muted-foreground">{a.hint}</span>}
          </Button>
        ))}
      </div>

      <p className="text-xs text-amber-800">
        <Trans t={t} i18nKey="devLogin.hint" components={{ code: <code className="font-mono" /> }} />
      </p>
    </div>
  );
}
